'use client';
import React, { useEffect } from 'react';
import { Settings, X, Play } from 'lucide-react';
import {
  DURATION_OPTIONS,
  EXAM_TOGGLES,
  type ExamDuration,
  type ExamPreset,
} from '@/data/examPresets';
import type { ExamConfig } from './GovtTypingTestClient';

interface ConfigModalProps {
  exam: ExamPreset;
  config: ExamConfig;
  onChange: (config: ExamConfig) => void;
  onClose: () => void;
  onStart: (config: ExamConfig) => void;
}

function Toggle({ on, onToggle, label }: { on: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onToggle}
      className="relative inline-flex h-5 w-9 shrink-0 items-center transition-colors"
      style={{
        backgroundColor: on ? 'var(--foreground)' : 'transparent',
        border: `1px solid ${on ? 'var(--foreground)' : 'var(--border-strong)'}`,
        borderRadius: 3,
      }}
    >
      <span
        className="inline-block h-3 w-3 transition-transform"
        style={{
          backgroundColor: on ? 'var(--background)' : 'var(--border-strong)',
          borderRadius: 1,
          transform: on ? 'translateX(1.25rem)' : 'translateX(0.175rem)',
        }}
      />
    </button>
  );
}

export default function ConfigModal({
  exam,
  config,
  onChange,
  onClose,
  onStart,
}: ConfigModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setDuration = (duration: ExamDuration) => onChange({ ...config, duration });
  const toggle = (id: keyof ExamConfig['toggles']) =>
    onChange({ ...config, toggles: { ...config.toggles, [id]: !config.toggles[id] } });

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(20,19,15,0.35)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Test settings"
    >
      <div
        className="layer-float w-full max-w-md overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between gap-3 px-5 py-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-3">
            <span className="icon-tile" style={{ width: '2rem', height: '2rem' }}>
              <Settings size={15} />
            </span>
            <div>
              <div className="eyebrow">Test settings</div>
              <h3 className="text-sm font-semibold text-foreground leading-snug mt-1">
                {exam.name}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost p-1.5 -mr-1.5"
            aria-label="Close settings"
          >
            <X size={17} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          {/* Duration */}
          <div className="mb-6">
            <div className="eyebrow mb-3">Test duration</div>
            <div className="grid grid-cols-4 gap-2">
              {DURATION_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  className={`duration-toggle justify-center ${config.duration === d ? 'active' : ''}`}
                >
                  {d} min
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="eyebrow mb-1">Options</div>
          <div>
            {EXAM_TOGGLES.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between gap-5 py-3.5"
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <div>
                  <div className="text-sm font-medium text-foreground">{t.label}</div>
                  <div className="text-xs text-faint mt-0.5">{t.helper}</div>
                </div>
                <Toggle on={config.toggles[t.id]} onToggle={() => toggle(t.id)} label={t.label} />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5">
          <button
            type="button"
            onClick={() => onStart(config)}
            className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 text-sm"
          >
            <Play size={15} />
            Start practice
          </button>
        </div>
      </div>
    </div>
  );
}
